import { type StateCreator } from 'zustand';

import { type PendingSkillInstruction } from '../../types/pendingSkillInstruction';
import { type MentionItem } from '../../ui/components/ChatComposer/mentions';

export type ChatComposerSlice = {
  /** Registered by the ChatComposer on mount; called to convert all draft tokens to committed. */
  commitTokens: (() => void) | null;
  /** Registered by the ChatComposer on mount; called to programmatically insert a token. */
  insertToken: ((item: MentionItem) => void) | null;
  /** Registered by the ChatComposer on mount; removes a token by ID regardless of draft status. */
  removeToken: ((tokenId: string) => void) | null;
  /** Registered by the ChatComposer on mount; replaces all draft tokens with the given set. */
  syncTokens: ((items: MentionItem[]) => void) | null;
  /** Registered by the ChatComposer on mount; replaces all text in the editor (keeping tokens) without submitting. */
  messageText: ((text: string) => void) | null;
  /** True while files are being uploaded to the chat composer via drag-and-drop. */
  isUploadingToChat: boolean;
  /** Cleared by AgentProvider after sendMessage; keyed by slash command so re-selecting replaces stale copy. */
  pendingSkillInstructions: PendingSkillInstruction[];
  /** Queued @-mention set before the ChatComposer mounts; drained on mount. */
  pendingToken: MentionItem | null;
  setCommitTokens: (fn: (() => void) | null) => void;
  setMessageText: (fn: ((text: string) => void) | null) => void;
  setInsertToken: (fn: ((item: MentionItem) => void) | null) => void;
  setIsUploadingToChat: (value: boolean) => void;
  addPendingSkillInstructions: (skill: PendingSkillInstruction) => void;
  clearPendingSkillInstructions: () => void;
  setPendingToken: (item: MentionItem | null) => void;
  setRemoveToken: (fn: ((tokenId: string) => void) | null) => void;
  setSyncTokens: (fn: ((items: MentionItem[]) => void) | null) => void;
  composerTokenTypes: string[];
  setComposerTokenTypes: (types: string[]) => void;
  /**
   * Whether the @-mention or / skill suggestion list is open in the chat composer.
   * Updated synchronously from @tiptap/suggestion (so canvas Escape can observe it in capture phase).
   */
  chatMentionSuggestionOpen: boolean;
  chatSlashCommandSuggestionOpen: boolean;
  setChatMentionSuggestionOpen: (open: boolean) => void;
  setChatSlashCommandSuggestionOpen: (open: boolean) => void;
};

export type CreateChatComposerSlice = () => StateCreator<ChatComposerSlice, [], [], ChatComposerSlice>;

export const createChatComposerSlice: CreateChatComposerSlice = () => (set) => ({
  addPendingSkillInstructions: (skill) =>
    set((state) => {
      const withoutSame = state.pendingSkillInstructions.filter((e) => e.slashCommand !== skill.slashCommand);

      return {
        pendingSkillInstructions: [...withoutSame, skill],
      };
    }),
  chatMentionSuggestionOpen: false,
  chatSlashCommandSuggestionOpen: false,
  clearPendingSkillInstructions: () => set({ pendingSkillInstructions: [] }),
  commitTokens: null,
  composerTokenTypes: [],
  insertToken: null,
  isUploadingToChat: false,
  messageText: null,
  pendingSkillInstructions: [],
  pendingToken: null,
  removeToken: null,
  setChatMentionSuggestionOpen: (open) => set({ chatMentionSuggestionOpen: open }),
  setChatSlashCommandSuggestionOpen: (open) => set({ chatSlashCommandSuggestionOpen: open }),
  setCommitTokens: (fn) => set({ commitTokens: fn }),
  setComposerTokenTypes: (types) => set({ composerTokenTypes: types }),
  setInsertToken: (fn) => set({ insertToken: fn }),
  setIsUploadingToChat: (value) => set({ isUploadingToChat: value }),
  setMessageText: (fn) => set({ messageText: fn }),
  setPendingToken: (item) => set({ pendingToken: item }),
  setRemoveToken: (fn) => set({ removeToken: fn }),
  setSyncTokens: (fn) => set({ syncTokens: fn }),
  syncTokens: null,
});
